import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { I18NextNS } from 'src/models/Language.model';

import {
  type DateInput,
  DEFAULT_DATE_FORMAT,
  DEFAULT_DATE_TIME_FORMAT,
  DEFAULT_FILE_DATE_FORMAT,
  DEFAULT_TIME_FORMAT,
  formatIsoDate,
  formatTodayForFile,
  formatUtcDateTime,
} from 'src/utils/formatDate';

const KEY_PREFIX = 'dateFormats';

const resolveFormat = (value: string, tail: string, fallback: string) =>
  value && value !== `${KEY_PREFIX}.${tail}` && value !== tail
    ? value
    : fallback;

const useDateFormats = () => {
  const { t } = useTranslation(I18NextNS.COMMON);

  return useMemo(() => {
    const dateFormat = resolveFormat(
      t(`${KEY_PREFIX}.date`),
      'date',
      DEFAULT_DATE_FORMAT,
    );
    const timeFormat = resolveFormat(
      t(`${KEY_PREFIX}.time`),
      'time',
      DEFAULT_TIME_FORMAT,
    );
    const dateTimeFormat = resolveFormat(
      t(`${KEY_PREFIX}.dateTime`),
      'dateTime',
      DEFAULT_DATE_TIME_FORMAT,
    );
    const fileDateFormat = resolveFormat(
      t(`${KEY_PREFIX}.fileDate`),
      'fileDate',
      DEFAULT_FILE_DATE_FORMAT,
    );

    return {
      dateFormat,
      timeFormat,
      dateTimeFormat,
      fileDateFormat,
      formatDate: (value: DateInput) => formatIsoDate(value, dateFormat),
      formatDateTime: (value: DateInput) =>
        formatUtcDateTime(value, dateTimeFormat),
      formatDatePart: (value: DateInput) =>
        formatUtcDateTime(value, dateFormat),
      formatTimePart: (value: DateInput) =>
        formatUtcDateTime(value, timeFormat),
      formatTodayForFile: () => formatTodayForFile(fileDateFormat),
    };
  }, [t]);
};

export default useDateFormats;
